import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { AppShell } from "@/components/AppShell";
import { IMPACT } from "@/lib/impact-data";
import { Printer, Download, FileDown, TrendingUp, Users, Building2, Coins, Sparkles, Target, Globe2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REPORT_ID = "impact-report-root";

async function downloadImpactPdf() {
  const node = document.getElementById(REPORT_ID);
  if (!node) throw new Error("Report node not found");

  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import("html2canvas-pro"),
    import("jspdf"),
  ]);

  // Force light background for capture (avoids transparency artifacts).
  const bg = getComputedStyle(document.body).backgroundColor || "#ffffff";

  const canvas = await html2canvas(node, {
    backgroundColor: bg,
    scale: 2,
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
  });

  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW;
  const imgH = (canvas.height * imgW) / canvas.width;

  // Slice the tall canvas into A4 pages.
  const pageHeightPx = (pageH * canvas.width) / pageW;
  let remaining = canvas.height;
  let offset = 0;
  const ctxW = canvas.width;

  while (remaining > 0) {
    const sliceH = Math.min(pageHeightPx, remaining);
    const slice = document.createElement("canvas");
    slice.width = ctxW;
    slice.height = sliceH;
    const sctx = slice.getContext("2d");
    if (!sctx) break;
    sctx.fillStyle = bg;
    sctx.fillRect(0, 0, ctxW, sliceH);
    sctx.drawImage(canvas, 0, offset, ctxW, sliceH, 0, 0, ctxW, sliceH);
    const dataUrl = slice.toDataURL("image/jpeg", 0.92);
    const sliceMm = (sliceH * imgW) / ctxW;
    if (offset > 0) pdf.addPage();
    pdf.addImage(dataUrl, "JPEG", 0, 0, imgW, sliceMm);
    offset += sliceH;
    remaining -= sliceH;
  }

  const date = new Date().toISOString().slice(0, 10);
  pdf.save(`inkubera-impact-report-${date}.pdf`);
}

export const Route = createFileRoute("/impact")({
  head: () => ({
    meta: [
      { title: "Impact Report — Inkubera Örebro" },
      { name: "description", content: "Inkubera Örebros samhällsbidrag sedan 2007 — bolag, kapital, sysselsättning och hållbarhet." },
    ],
  }),
  component: ImpactPage,
});

// ------------ helpers ------------
const fmtSEK = (tkr: number) => {
  // input in tkr
  if (Math.abs(tkr) >= 1_000_000) return `${(tkr / 1_000_000).toFixed(1)} mdkr`;
  if (Math.abs(tkr) >= 1_000) return `${(tkr / 1_000).toFixed(1)} Mkr`;
  return `${Math.round(tkr).toLocaleString("sv-SE")} tkr`;
};
const fmtNum = (n: number) => Math.round(n).toLocaleString("sv-SE");

const SECTOR_LABEL: Record<string, string> = {
  software_and_ict: "Mjukvara & ICT",
  lifescience: "Lifescience",
  industrial_technology: "Industriteknik",
  environment_and_energy: "Miljö & energi",
  experience: "Upplevelse",
  agro: "Agro",
  other: "Övrigt",
  okänd: "Okänd",
};
const IDEA_LABEL: Record<string, string> = {
  business: "Näringsliv",
  university: "Universitet",
  innovator: "Innovatör",
  public_sector: "Offentlig sektor",
  research: "Forskning",
  okänd: "Okänd källa",
};
const FIN_TYPE_LABEL: Record<string, string> = {
  grant: "Bidrag",
  loan: "Lån",
  ownership: "Ägarkapital",
};
const CHART_COLORS = [
  "var(--chart-1)", "var(--chart-2)", "var(--chart-3)",
  "var(--chart-4)", "var(--chart-5)", "var(--primary)", "var(--accent)",
];

function ImpactPage() {
  return (
    <AppShell>
      <article id={REPORT_ID} className="bg-background">
        <Hero />
        <SectionTimeline />
        <SectionDealflow />
        <SectionEconomy />
        <SectionFinancing />
        <SectionGender />
        <SectionSDG />
        <SectionIRL />
        <SectionIncubator />
        <SectionAlumni />
        <SectionFooter />
      </article>
    </AppShell>
  );
}

// ============= HERO =============
function Hero() {
  const h = IMPACT.hero;
  const [downloading, setDownloading] = useState(false);

  const handlePdf = async () => {
    setDownloading(true);
    const t = toast.loading("Genererar PDF-rapport…");
    try {
      await downloadImpactPdf();
      toast.success("Rapport nedladdad", { id: t });
    } catch (e) {
      console.error(e);
      toast.error("Kunde inte skapa PDF", { id: t, description: e instanceof Error ? e.message : undefined });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="px-4 sm:px-8 pt-10 pb-12 border-b border-border bg-gradient-to-b from-secondary/40 to-transparent">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
            Impact Report · {IMPACT.meta.incubator}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            {h.yearMax - h.yearMin + 1} år av entreprenörskap i Örebro
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Sedan {h.yearMin} har {h.admittedTotal} bolag antagits till inkubatorn.
            {" "}{h.alumni} har tagit examen som alumner, {h.ongoing} är aktiva idag.
            Tillsammans har portföljen mobiliserat {fmtSEK(h.totalCapital)} i kapital.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap" data-html2canvas-ignore="true">
          <button
            onClick={() => window.print()}
            className="h-10 px-4 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/70 inline-flex items-center gap-2"
          >
            <Printer className="size-4" /> Skriv ut
          </button>
          <button
            onClick={handlePdf}
            disabled={downloading}
            className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
            {downloading ? "Skapar PDF…" : "Ladda ner PDF"}
          </button>
          <button
            onClick={() => navigator.share?.({ title: "Inkubera Impact Report", url: window.location.href })}
            className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 inline-flex items-center gap-2"
          >
            <Download className="size-4" /> Dela
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={<Building2 className="size-4" />} label="Antagna bolag" value={fmtNum(h.admittedTotal)} sub={`Sedan ${h.yearMin}`} />
        <Kpi icon={<Sparkles className="size-4" />} label="Alumner" value={fmtNum(h.alumni)} sub={`${h.survivalPct}% överlevnad`} />
        <Kpi icon={<TrendingUp className="size-4" />} label="Aktiva nu" value={fmtNum(h.ongoing)} sub="Pågående inkubation" />
        <Kpi icon={<Coins className="size-4" />} label="Mobiliserat kapital" value={fmtSEK(h.totalCapital)} sub={`${Math.round(100*h.privateCap/h.totalCapital)}% privat`} />
        <Kpi icon={<Users className="size-4" />} label={`Sysselsatta ${h.lastYear}`} value={fmtNum(h.lyEmployed)} sub={`I ${fmtNum(h.lyEmployed > 0 ? Math.round(h.admittedTotal*0.3) : 0)} aktiva bolag`} />
        <Kpi icon={<Globe2 className="size-4" />} label="Omsättning portfölj" value={fmtSEK(h.lyRevenue)} sub={`Senast rapporterat ${h.lastYear}`} />
      </div>
    </section>
  );
}

function Kpi({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="p-4 rounded-2xl bg-card border border-border">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-display text-2xl leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function SectionHeader({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-5">
      <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">{eyebrow}</div>
      <h2 className="font-display text-2xl sm:text-3xl">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">{subtitle}</p>}
    </div>
  );
}

function Panel({ title, hint, children, className = "" }: { title: string; hint?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-card border border-border p-5 ${className}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-display text-base">{title}</h3>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  fontSize: 12,
  color: "var(--popover-foreground)",
} as const;

// ============= 2. TIDSLINJE =============
function SectionTimeline() {
  const data = IMPACT.timeline;
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Tidslinje" title="Pågående bolag per år"
        subtitle="Antal IBO-relationer som var pågående vid årets slut, brutet på status. Antagna och lämnade markeras separat." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" title="Aktiv kohort över tid" hint="stackat">
          <div className="h-80">
            <ResponsiveContainer>
              <AreaChart data={data} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="ongoing" name="Aktiva" stackId="1" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.75} />
                <Area type="monotone" dataKey="alumni" name="Alumner (samma år)" stackId="1" stroke="var(--chart-2)" fill="var(--chart-2)" fillOpacity={0.6} />
                <Area type="monotone" dataKey="canceled" name="Avbrutna" stackId="1" stroke="var(--chart-3)" fill="var(--chart-3)" fillOpacity={0.45} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="In och ut per år" hint="bolag">
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="admitted" name="Antagna" fill="var(--chart-1)" radius={[4,4,0,0]} />
                <Bar dataKey="left" name="Lämnade" fill="var(--chart-4)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </section>
  );
}

// ============= 3. DEALFLOW =============
function SectionDealflow() {
  const d = IMPACT.dealflow;
  const ideaData = Object.entries(d.ideaSources).map(([k, v]) => ({ name: IDEA_LABEL[k] ?? k, value: v }));
  const sectorData = d.sectorByYear as Array<Record<string, number | string>>;
  const incData = d.incTime as Array<{ year: number; avgDays: number }>;
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Dealflow" title="Var idéerna kommer från och vad vi tar in"
        subtitle="Sektor- och idékällsfördelning för antagna bolag samt snittinkubationstid per avgångskohort." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Idékällor" hint={`${IMPACT.hero.admittedTotal} antagna totalt`}>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={ideaData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {ideaData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel className="lg:col-span-2" title="Antagna per sektor och år" hint="stackat">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={sectorData} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {d.allSectors.map((s, i) => (
                  <Bar key={s} dataKey={s} name={SECTOR_LABEL[s] ?? s} stackId="s" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Snittinkubationstid" hint="dagar per avgångskohort">
          <div className="h-60">
            <ResponsiveContainer>
              <LineChart data={incData} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v} dagar`} />
                <Line type="monotone" dataKey="avgDays" stroke="var(--chart-1)" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Deeptech-andel" hint="% av antagna">
          <div className="h-60">
            <ResponsiveContainer>
              <BarChart data={d.deeptechByYear} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit="%" />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v}%`} />
                <Bar dataKey="pct" fill="var(--chart-2)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Kvinnligt ägande vid antagning" hint="% genomsnitt">
          <div className="h-60">
            <ResponsiveContainer>
              <LineChart data={d.femaleOwnership} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit="%" />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v}%`} />
                <Line type="monotone" dataKey="avgPct" stroke="var(--chart-4)" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </section>
  );
}

// ============= 4. EKONOMI =============
function SectionEconomy() {
  const data = IMPACT.economy;
  const h = IMPACT.hero;
  // cumulative wages
  const cum: Array<{ year: number; cum: number }> = [];
  let sum = 0;
  for (const r of data) { sum += r.wages; cum.push({ year: r.year, cum: sum }); }
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Ekonomi" title="Vad portföljen producerar för Örebro"
        subtitle="Aggregerade nyckeltal från InkRapp och Roaring/UC. Belopp i tkr." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Nettoomsättning i portföljen" hint="tkr per år, summerat">
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={data} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => fmtSEK(v)} width={70} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtSEK(v)} />
                <Area type="monotone" dataKey="revenue" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.3} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Anställda (kön)" hint="rapporterat per år">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="men" name="Män" stackId="g" fill="var(--chart-1)" />
                <Bar dataKey="women" name="Kvinnor" stackId="g" fill="var(--chart-4)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Kumulativa lönekostnader" hint="bidrag till samhället">
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={cum} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtSEK} width={70} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtSEK(v)} />
                <Area type="monotone" dataKey="cum" name="Kumulativt" stroke="var(--chart-2)" fill="var(--chart-2)" fillOpacity={0.3} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
      <div className="mt-4 p-5 rounded-2xl bg-secondary/40 border border-border text-sm">
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <Metric label="Total lönesumma 2007–idag" value={fmtSEK(h.cumWages)} />
          <Metric label={`Omsättning ${h.lastYear}`} value={fmtSEK(h.lyRevenue)} />
          <Metric label={`Lönesumma ${h.lastYear}`} value={fmtSEK(h.lyWages)} />
          <Metric label={`Sysselsatta ${h.lastYear}`} value={fmtNum(h.lyEmployed)} />
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}

// ============= 5. FINANSIERING =============
function SectionFinancing() {
  const f = IMPACT.financing;
  const byYear = f.byYear.map((r) => ({
    year: r.year,
    Privat: +(r.private / 1000).toFixed(1),
    Offentligt: +(r.public / 1000).toFixed(1),
  }));
  const byType = Object.entries(f.byType).map(([k, v]) => ({
    name: FIN_TYPE_LABEL[k] ?? k, Mkr: +(v / 1000).toFixed(1),
  }));
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Kapital" title="Mobiliserad finansiering"
        subtitle={`${fmtSEK(f.total)} har flödat till portföljen i ${f.nInvestments} investeringar — ${Math.round(100*f.totalPrivate/f.total)}% privat kapital och ${Math.round(100*f.totalPublic/f.total)}% offentligt stöd.`} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" title="Privat vs offentligt per år" hint="Mkr, stackat">
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={byYear} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit=" Mkr" />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Offentligt" stackId="a" fill="var(--chart-2)" />
                <Bar dataKey="Privat" stackId="a" fill="var(--chart-1)" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Per finansieringstyp" hint="Mkr, alla år">
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={byType} layout="vertical" margin={{ left: 20, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" horizontal={false} />
                <XAxis type="number" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit=" Mkr" />
                <YAxis dataKey="name" type="category" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v} Mkr`} />
                <Bar dataKey="Mkr" fill="var(--foreground)" radius={[0,6,6,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </section>
  );
}

// ============= 6. JÄMSTÄLLDHET =============
function SectionGender() {
  const data = IMPACT.gender.filter((r) => r.boardMen + r.boardWomen > 0);
  const ceoData = data.map((r) => ({
    year: r.year,
    "VD män": r.ceoMen,
    "VD kvinnor": r.ceoWomen,
    "Ordf män": r.chairMen,
    "Ordf kvinnor": r.chairWomen,
  }));
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Jämställdhet & mångfald" title="Könsfördelning i ledning"
        subtitle="Rapporterad fördelning för portföljbolagens styrelser, VD och ordförande per år." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Styrelseledamöter" hint="antal personer, summerat">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="boardMen" name="Män" stackId="b" fill="var(--chart-1)" />
                <Bar dataKey="boardWomen" name="Kvinnor" stackId="b" fill="var(--chart-4)" />
                <Bar dataKey="boardOther" name="Övriga" stackId="b" fill="var(--chart-3)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="VD och ordförande" hint="antal bolag">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={ceoData} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="VD män" fill="var(--chart-1)" />
                <Bar dataKey="VD kvinnor" fill="var(--chart-4)" />
                <Bar dataKey="Ordf män" fill="var(--chart-2)" />
                <Bar dataKey="Ordf kvinnor" fill="var(--chart-5)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </section>
  );
}

// ============= 7. SDG =============
function SectionSDG() {
  const counts = IMPACT.sdg.counts;
  const sorted = Object.entries(counts).sort(([, a], [, b]) => b - a);
  const max = sorted[0]?.[1] ?? 1;
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Hållbarhet" title="SDG-bidrag i portföljen"
        subtitle={`${IMPACT.sdg.companiesWithSdg} av ${IMPACT.sdg.totalSdgCompanies} bolag har deklarerat bidrag till minst ett av FN:s 17 globala mål.`} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map(([name, n], i) => (
          <div key={name} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-sm font-medium leading-snug">{name}</div>
              <div className="font-display text-2xl tabular-nums">{n}</div>
            </div>
            <div className="h-1.5 mt-3 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(n / max) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============= 8. IRL =============
function SectionIRL() {
  const m = IMPACT.irl.movement;
  const radar = [
    { dim: "BRL", Vid_antagning: m.BRL.first, Senaste: m.BRL.last },
    { dim: "CRL", Vid_antagning: m.CRL.first, Senaste: m.CRL.last },
    { dim: "TMRL", Vid_antagning: m.TMRL.first, Senaste: m.TMRL.last },
    { dim: "SRL", Vid_antagning: m.SRL.first, Senaste: m.SRL.last },
  ];
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Mognadsutveckling (IRL)" title="Hur långt har bolagen flyttat sig?"
        subtitle={`Genomsnitt av första vs senaste utvärdering för ${m.BRL.n} bolag. Skalan går 1–9 i samtliga dimensioner.`} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" title="Före vs efter" hint="snitt per dimension">
          <div className="h-80">
            <ResponsiveContainer>
              <RadarChart data={radar}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="dim" stroke="var(--muted-foreground)" fontSize={12} />
                <PolarRadiusAxis domain={[0, 9]} stroke="var(--muted-foreground)" fontSize={10} />
                <Radar name="Vid antagning" dataKey="Vid_antagning" stroke="var(--chart-3)" fill="var(--chart-3)" fillOpacity={0.25} />
                <Radar name="Senaste" dataKey="Senaste" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.35} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <div className="grid grid-cols-2 gap-3">
          {(["BRL","CRL","TMRL","SRL"] as const).map((k) => (
            <div key={k} className="p-4 rounded-2xl bg-card border border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{k}</div>
              <div className="font-display text-2xl mt-1">+{m[k].delta}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{m[k].first} → {m[k].last}</div>
            </div>
          ))}
        </div>
      </div>

      <Panel title="Snittmognad i pågående kohort per år" hint="bara år med ≥5 utvärderingar" className="mt-4">
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={IMPACT.irl.byYear.filter((r) => r.n >= 5)} margin={{ left: -16, right: 8, top: 8 }}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} domain={[0, 9]} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line dataKey="BRL" stroke="var(--chart-1)" strokeWidth={2} dot={{ r: 2 }} />
              <Line dataKey="CRL" stroke="var(--chart-2)" strokeWidth={2} dot={{ r: 2 }} />
              <Line dataKey="TMRL" stroke="var(--chart-4)" strokeWidth={2} dot={{ r: 2 }} />
              <Line dataKey="SRL" stroke="var(--chart-5)" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </section>
  );
}

// ============= 9. INKUBATORN =============
function SectionIncubator() {
  const data = IMPACT.incubator.map((r) => ({
    year: r.year,
    Privat: +(r.private / 1000).toFixed(1),
    Universitet: +(r.university / 1000).toFixed(1),
    Regional: +(r.regional / 1000).toFixed(1),
    Nationell: +(r.national / 1000).toFixed(1),
    EU: +(r.eu / 1000).toFixed(1),
  }));
  const ideas = IMPACT.incubator.map((r) => ({
    year: r.year, Akademi: r.ideasAcademy, Näringsliv: r.ideasBusiness,
  }));
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Inkubatorn själv" title="Finansiering av verksamheten och idéflöde"
        subtitle="Hur Inkuberas drift har finansierats och hur många idéer som inkommit till screening per år." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2" title="Finansiering av Inkubera" hint="Mkr, stackat">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} unit=" Mkr" />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Privat" stackId="i" fill="var(--chart-1)" />
                <Bar dataKey="Universitet" stackId="i" fill="var(--chart-2)" />
                <Bar dataKey="Regional" stackId="i" fill="var(--chart-3)" />
                <Bar dataKey="Nationell" stackId="i" fill="var(--chart-4)" />
                <Bar dataKey="EU" stackId="i" fill="var(--chart-5)" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Idéer in per år" hint="screenade ärenden">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={ideas} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Akademi" stackId="x" fill="var(--chart-2)" />
                <Bar dataKey="Näringsliv" stackId="x" fill="var(--chart-1)" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </section>
  );
}

// ============= 10. ALUMNI =============
function SectionAlumni() {
  const list = IMPACT.alumniTop;
  return (
    <section className="px-4 sm:px-8 py-10 border-b border-border">
      <SectionHeader eyebrow="Alumni-spotlight" title="Bolag som har vuxit efter inkubation"
        subtitle="Topp-alumner sorterade efter senaste rapporterade nettoomsättning. Belopp i tkr." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((a) => (
          <div key={a.name} className="p-4 rounded-xl bg-card border border-border flex items-center gap-4">
            <div className="size-12 rounded-xl flex items-center justify-center font-display text-base bg-gradient-to-br from-primary/80 to-accent/80 text-primary-foreground shrink-0">
              {a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {a.sector ? (SECTOR_LABEL[a.sector] ?? a.sector) : "—"} · alumn {a.leftYear}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-base">{fmtSEK(a.revenue)}</div>
              <div className="text-[11px] text-muted-foreground">{a.employees} anställda</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============= FOOTER =============
function SectionFooter() {
  return (
    <section className="px-4 sm:px-8 py-10">
      <div className="rounded-2xl bg-secondary/40 border border-border p-6 flex flex-wrap items-start gap-6 justify-between">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 mb-2">
            <Target className="size-4 text-primary" />
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Metod & källa</div>
          </div>
          <p className="text-sm text-muted-foreground">
            Data är exporterad {IMPACT.meta.exportDate} ur InkRapp och kompletterad med externa nyckeltal från Roaring/UC.
            Inkubationsstatus mäts vid årets slut. "Mobiliserat kapital" summerar alla registrerade finansieringsrundor i portföljen
            och inkluderar både privat ägarkapital, lån och offentliga bidrag.
          </p>
        </div>
        <div className="text-right">
          <div className="font-display text-lg">{IMPACT.meta.incubator}</div>
          <div className="text-xs text-muted-foreground">Portfolio Intelligence · {IMPACT.hero.yearMin}–{IMPACT.hero.yearMax}</div>
        </div>
      </div>
    </section>
  );
}
